<template>
  <section
    class="section"
    :class="{ 'section--hero': hero }"
    :id="id"
  >
    <div
      class="section__side"
      :class="{ 'section__side--separator': hero }"
    >
      <div class="section__sticky">
        <div class="section__title">
          <span
            v-if="index"
            class="section__index"
          >
            {{ count }}
          </span>
          <span>
            {{ title }}
          </span>
        </div>
        <div
          v-if="decoration"
          :style="{ maskImage: `url(${decoration})` }"
          class="section__decoration"
          alt=""
        />
      </div>
    </div>
    <div
      class="section__content"
      :data-aos="hero ? '' : 'fade-up'"
    >
      <slot />
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue';

const props = defineProps<{
  decoration: string;
  hero?: boolean;
  id?: string;
  index?: number;
  title: string;
}>();

const count = computed(() => {
  if (props.index) {
    return props.index.toString().padStart(2, '0');
  }

  return '';
});
</script>

<style lang="scss" scoped>
@use '@/styles/mixins';
@use '@/styles/screens';

.section {
  display: grid;
  grid-template-columns: 1fr;
  position: relative;
  width: 100%;
  
  @include screens.tablet {
    grid-template-columns: var(--layout-gutter) auto;
  }
}

.section--hero {
  min-height: 100dvh;
}

.section__side {
  position: relative;
  padding-left: 24px;
  padding-right: 24px;

  @include screens.tablet {
    padding-left: 32px;
    padding-right: 16px;
  }
}

.section__side--separator {
  border-right: 1px solid var(--color-border);
}

.section__sticky {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 32px 0 24px;
  
  @include screens.tablet {
    margin: 0;
    position: sticky;
    top: 80px;
  }
}

.section__title {  
  display: flex;
  gap: 8px;

  @include mixins.monotext;
}

.section__index {
  color: var(--color-highlight);
}

.section__decoration {
  background-color: var(--color-text-strong);
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
  position: absolute;
  transform: translateY(-20%);
  height: 40px;
  width: 40px;

  @include screens.tablet {
    position: unset;
    transform: unset;
    height: 64px;
    width: 64px;
  }
}

.section__content {
  padding-left: 24px;
  padding-right: 24px;
  max-width: var(--layout-max-width);

  @include screens.tablet {
    padding-left: 60px;
  }

  @include screens.desktop {
    padding-left: 120px;
  }
}

.section__side,
.section__content {
  padding-top: 20px;
  padding-bottom: 20px;

  @include screens.tablet {
    padding-top: 80px;
    padding-bottom: 80px;
  }
}

.section--hero .section__side {
  display: none;

  @include screens.tablet {
    display: block;
  }
}

.section--hero .section__content {
  display: flex;
  flex-direction: column;
  justify-content: center;

  @include screens.tablet {
    display: block;
  }
}

.section--hero .section__side,
.section--hero .section__content {
  padding-top: 40px;

  @include screens.tablet {
    padding-top: 120px;
  }
}

.section--hero .section__side .section__title {
  @include screens.tablet {
    top: 120px;
  }
}
</style>
